import { context, trace } from '@opentelemetry/api';

const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Returns the host portion of a URL, or 'unknown' on parse failure.
 * Used for span attributes — querystrings carry signed-URL tokens (PII), so
 * we strip them before logging.
 */
const safeHost = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return 'unknown';
  }
};

/**
 * Fetches a remote resource and returns its body as a Uint8Array.
 *
 * Used to pull binary assets (PDF, XML) into memory so they can be packed
 * into a ZIP archive in the browser.
 *
 * @param url - Absolute URL to fetch
 * @returns Promise resolving to the response bytes
 * @throws Error when the response status is not 2xx
 */
export const fetchAsBytes = async (url: string): Promise<Uint8Array> => {
  const span = tracer.startSpan('SUB.fetchPaymentAsset', {
    attributes: { 'download.url': safeHost(url) },
  });
  const ctx = trace.setSpan(context.active(), span);

  return context.with(ctx, async () => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${safeHost(url)}: ${response.status}`);
      }
      const buffer = await response.arrayBuffer();
      return new Uint8Array(buffer);
    } finally {
      span.end();
    }
  });
};
